import { get, post } from './fetcher';
import { API_ENDPOINTS } from '../config/api-config';

/**
 * 웹 지역 API
 */

/**
 * 웹 지역 목록 조회
 * @param {object} params - 조회 파라미터
 * @param {string} params.snapshotDate - 조회할 스냅샷 날짜 (null이면 최신 데이터)
 * @param {string} params.sidoName - 시도명 필터
 * @param {string} params.sigunguName - 시군구명 필터
 * @param {string} params.bjdCode - 법정동코드 필터
 * @param {number} params.pageNumber - 페이지 번호 (1부터 시작)
 * @param {number} params.pageSize - 페이지당 행 수 (최대 1000)
 * @returns {Promise<Array>} 지역 목록
 */
export const getRegionList = async (params = {}) => {
  const {
    snapshotDate = null,
    sidoName = null,
    sigunguName = null,
    bjdCode = null,
    pageNumber = 1,
    pageSize = 20,
  } = params;

  return get(API_ENDPOINTS.REGION.LIST, {
    snapshotDate,
    sidoName,
    sigunguName,
    bjdCode,
    pageNumber,
    pageSize,
  });
};

/**
 * 웹 지역 데이터 신규 등록 (INSERT)
 * @param {object} data - 등록할 데이터
 * @returns {Promise<object>} 저장 결과
 */
export const insertRegion = async (data) => {
  const requestData = {
    mode: 'INSERT',
    snapshotDate: data.snapshotDate,
    sourceName: data.sourceName || 'excel',
    bjdCode: data.bjdCode,

    livingZone: data.livingZone,
    regionGrade: data.regionGrade,
    dongName: data.dongName,
    dongGrade: data.dongGrade,

    regulation: data.regulation,
    regulationDate: data.regulationDate,
    preferOrder: data.preferOrder,

    popGrade: data.popGrade,
    popTotal: data.popTotal,
    householdTotal: data.householdTotal,
    popMigration: data.popMigration,

    jobGrade: data.jobGrade,
    workerCnt: data.workerCnt,
    companyCnt: data.companyCnt,
    company500pCnt: data.company500pCnt,

    trafficGrade: data.trafficGrade,
    schoolGrade: data.schoolGrade,
    envGrade: data.envGrade,

    supplyGrade: data.supplyGrade,
    supplyRate: data.supplyRate,
    demand3y: data.demand3y,
    movein3y: data.movein3y,
    supply2025: data.supply2025,
    supply2026: data.supply2026,
    supply2027: data.supply2027,

    issueSign: data.issueSign,
    issueNote: data.issueNote,

    yearEndSource: data.yearEndSource,

    avgJeonseRatio: data.avgJeonseRatio,
    avgMaemaeTxCnt: data.avgMaemaeTxCnt,
    avgJeonseTxCnt: data.avgJeonseTxCnt,
  };

  return post(API_ENDPOINTS.REGION.SAVE, requestData);
};

/**
 * 웹 지역 데이터 수정 (UPDATE)
 * @param {object} data - 수정할 데이터 (snapshotId 포함)
 * @returns {Promise<object>} 수정 결과
 */
export const updateRegion = async (data) => {
  const requestData = {
    mode: 'UPDATE',
    snapshotId: data.snapshotId,
    ...data,
  };

  return post(API_ENDPOINTS.REGION.SAVE, requestData);
};

/**
 * 웹 지역 데이터 삭제 (DELETE)
 * @param {number} snapshotId - 스냅샷 ID
 * @returns {Promise<object>} 삭제 결과
 */
export const deleteRegion = async (snapshotId) => {
  const requestData = {
    mode: 'DELETE',
    snapshotId,
  };

  return post(API_ENDPOINTS.REGION.SAVE, requestData);
};

/**
 * 특정 지역 조회 (법정동코드별)
 * @param {string} bjdCode - 법정동코드
 * @param {string} snapshotDate - 스냅샷 날짜 (옵션)
 * @returns {Promise<object>} 지역 정보
 */
export const getRegionByCode = async (bjdCode, snapshotDate = null) => {
  const result = await getRegionList({
    bjdCode,
    snapshotDate,
    pageNumber: 1,
    pageSize: 1,
  });

  return result.length > 0 ? result[0] : null;
};

/**
 * 시도별 지역 목록 조회
 * @param {string} sidoName - 시도명
 * @param {number} pageNumber - 페이지 번호
 * @param {number} pageSize - 페이지당 행수
 * @returns {Promise<Array>} 지역 목록
 */
export const getRegionsBySido = async (sidoName, pageNumber = 1, pageSize = 100) => {
  return getRegionList({
    sidoName,
    pageNumber,
    pageSize,
  });
};

/**
 * 시군구별 지역 목록 조회
 * @param {string} sidoName - 시도명
 * @param {string} sigunguName - 시군구명
 * @param {number} pageNumber - 페이지 번호
 * @param {number} pageSize - 페이지당 행수
 * @returns {Promise<Array>} 지역 목록
 */
export const getRegionsBySigungu = async (sidoName, sigunguName, pageNumber = 1, pageSize = 100) => {
  return getRegionList({
    sidoName,
    sigunguName,
    pageNumber,
    pageSize,
  });
};
